import {Input, Space} from "@ui";
import {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import styles from "./email-input.styles.module.scss";

const EMAIL_DOMAINS = [
  "gmail.com",
  "hotmail.com",
  "yahoo.com",
  "outlook.com",
  "live.com",
  "icloud.com",
  "protonmail.com",
  "aol.com",
];

export const EmailInput = ({value = "", onChange, placeholder, style}) => {
  const {t} = useTranslation();
  const [localPart, domain] = value?.includes("@") ? value.split("@") : [value || "", "gmail.com"];
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({top: 0, left: 0, width: 0});
  const dropdownContentRef = useRef(null);
  const buttonRef = useRef(null);

  const isCustomDomain = domain && !EMAIL_DOMAINS.includes(domain);

  useEffect(() => {
    const handleClickOutside = e => {
      const isInsideButton = buttonRef.current?.contains(e.target);
      const isInsideDropdown = dropdownContentRef.current?.contains(e.target);
      if (!isInsideButton && !isInsideDropdown) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (dropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 160;
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.right - dropdownWidth,
        width: dropdownWidth,
      });
    }
  }, [dropdownOpen]);

  const handleLocalPartChange = e => {
    const newLocal = e.target.value;
    if (newLocal.includes("@")) {
      onChange?.(newLocal);
      return;
    }
    onChange?.(`${newLocal}@${domain || ""}`);
  };

  const handleDomainChange = newDomain => {
    setDropdownOpen(false);
    if (newDomain === "other") {
      onChange?.(localPart ? `${localPart}@` : "");
      return;
    }
    onChange?.(localPart ? `${localPart}@${newDomain}` : newDomain);
  };

  const isOtherMode = value?.endsWith("@") || isCustomDomain;
  const selectValue = isOtherMode ? "other" : domain || "gmail.com";

  return (
    <Space.Compact
      block
      className={styles.email_input_group}
    >
      <Input
        className={styles.ant_input}
        placeholder={selectValue === "other" ? t("loginEmailPlaceholder") : placeholder}
        value={selectValue === "other" ? value : localPart}
        onChange={selectValue === "other" ? e => onChange?.(e.target.value) : handleLocalPartChange}
      />
      {selectValue !== "other" && (
        <span
          className={styles.email_at_separator}
          aria-hidden="true"
        >
          @
        </span>
      )}
      <div
        className={`${styles.domain_select_wrapper} ${selectValue === "other" ? styles.domain_select_wrapper_other : ""}`}
        style={{
          ...style,
          flex: selectValue === "other" && !dropdownOpen ? 0.3 : 1,
        }}
      >
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className={styles.domain_select}
        >
          {selectValue === "other" ? t("loginEmailDomainOther") : selectValue}
        </button>
        {dropdownOpen &&
          createPortal(
            <div
              ref={dropdownContentRef}
              className={styles.domain_dropdown_container}
              style={{
                position: "fixed",
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
              }}
            >
              <ul className={styles.domain_dropdown}>
                {EMAIL_DOMAINS.map(d => (
                  <li key={d}>
                    <button
                      type="button"
                      className={styles.domain_dropdown_option}
                      onClick={() => handleDomainChange(d)}
                    >
                      {d}
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    type="button"
                    className={styles.domain_dropdown_option}
                    onClick={() => handleDomainChange("other")}
                  >
                    {t("loginEmailDomainOther")}
                  </button>
                </li>
              </ul>
            </div>,
            document.body
          )}
      </div>
    </Space.Compact>
  );
};
